import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import axios from "../../utils/api.js";

const QuestionsList = () => {
  const [questions, setQuestions] = useState([]);
  const [editQuestion, setEditQuestion] = useState(null);
  const [updatedData, setUpdatedData] = useState({
    questionNumber: "",
    question: "",
    answerType: "",
    options: [],
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const response = await axios.get("/questions", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try {
      const response = await axios.delete(`/deletedaranalysis/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      alert(response.data.message);
      setQuestions(questions.filter((q) => q._id !== id));
    } catch (error) {
      console.error("Error deleting question:", error);
    }
  };

  const handleEdit = (question) => {
    setEditQuestion(question.questionNumber);
    setUpdatedData({
      questionNumber: question.questionNumber,
      question: question.question,
      answerType: question.answerType,
      options: question.options || [],
    });
  };

  const handleCancel = () => {
    setEditQuestion(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...updatedData.options];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    setUpdatedData((prevData) => ({ ...prevData, options: updatedOptions }));
  };

  const handleAddOption = () => {
    setUpdatedData((prevData) => ({
      ...prevData,
      options: [...prevData.options, { option: "", weightage: 0 }],
    }));
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = updatedData.options.filter((_, i) => i !== index);
    setUpdatedData((prevData) => ({ ...prevData, options: updatedOptions }));
  };

  const handleUpdate = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(`/update-daranalysis/${editQuestion}`, updatedData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setEditQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error("Error updating question:", error);
    }
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.5rem 0.65rem",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
    marginBottom: 8,
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          overflow: "hidden",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>Loading...</span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        padding: "20px 32px 48px",
        boxSizing: "border-box",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Questions List
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View and manage DAR analysis questions
        </p>
      </div>

      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: questions.length === 0 ? "block" : "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          alignItems: "stretch",
        }}
      >
        {questions.length === 0 ? (
          <p
            style={{
              color: "#64748b",
              fontSize: 14,
              textAlign: "center",
              margin: 0,
              gridColumn: "1 / -1",
            }}
          >
            No questions yet.
          </p>
        ) : (
          [...questions]
            .sort((a, b) => {
              const numA = Number(a.questionNumber);
              const numB = Number(b.questionNumber);
              if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
              return String(a.questionNumber).localeCompare(String(b.questionNumber));
            })
            .map((q) => (
            <div
              key={q.questionNumber}
              style={{
                backgroundColor: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 24,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {editQuestion === q.questionNumber ? (
                <div>
                  <input
                    type="text"
                    name="question"
                    value={updatedData.question}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder="Enter question"
                  />
                  <select
                    name="answerType"
                    value={updatedData.answerType}
                    onChange={handleChange}
                    style={{
                      ...inputStyle,
                      cursor: "pointer",
                      appearance: "none",
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2364748b' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 0.65rem center",
                      backgroundSize: "1rem",
                      paddingRight: 36,
                    }}
                  >
                    <option value="text">Text</option>
                    <option value="multiple-choice">Multiple Choice</option>
                  </select>

                  {updatedData.answerType === "multiple-choice" &&
                    updatedData.options.map((opt, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <input
                          type="text"
                          value={opt.option}
                          onChange={(e) =>
                            handleOptionChange(index, "option", e.target.value)
                          }
                          style={{ ...inputStyle, flex: "2", marginBottom: 0 }}
                          placeholder="Option"
                        />
                        <input
                          type="number"
                          value={opt.weightage}
                          onChange={(e) =>
                            handleOptionChange(index, "weightage", Number(e.target.value))
                          }
                          style={{ ...inputStyle, flex: "1", marginBottom: 0, maxWidth: 80 }}
                          placeholder="Weight"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          style={{
                            padding: "0.4rem 0.6rem",
                            backgroundColor: "#dc2626",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}

                  {updatedData.answerType === "multiple-choice" && (
                    <button
                      type="button"
                      onClick={handleAddOption}
                      style={{
                        padding: "0.5rem 0.75rem",
                        fontSize: 13,
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                        marginBottom: 8,
                      }}
                    >
                      Add Option
                    </button>
                  )}

                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={handleUpdate}
                      style={{
                        padding: "0.5rem 1rem",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: "#f1f5f9",
                        color: "#64748b",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p
                    style={{
                      margin: "0 0 12px",
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      color: "#1e293b",
                      lineHeight: 1.4,
                    }}
                  >
                    Q{q.questionNumber}: {q.question}
                  </p>
                  {q.answerType === "multiple-choice" && q.options && q.options.length > 0 && (
                    <ul
                      style={{
                        margin: "0 0 14px",
                        paddingLeft: 22,
                        fontSize: 15,
                        color: "#64748b",
                        lineHeight: 1.5,
                      }}
                    >
                      {q.options.map((opt, index) => (
                        <li key={index} style={{ marginBottom: 4 }}>
                          {opt.option} {opt.weightage != null ? `(Weightage: ${opt.weightage})` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => handleEdit(q)}
                      style={{
                        padding: "6px 12px",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(q._id)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default QuestionsList;
